import { useAppTheme } from '@/shared/theme';
import { SxProps, Theme } from '@mui/material';
import {
  lightSessionTimelineTokens,
  darkSessionTimelineTokens,
  SessionTimelineThemeTokens,
} from './sessionTimelineTokens';

export function useSessionTimelineTheme() {
  const { mode } = useAppTheme();
  const tokens: SessionTimelineThemeTokens =
    mode === 'light' ? lightSessionTimelineTokens : darkSessionTimelineTokens;

  const getCardSx = (): SxProps<Theme> => ({
    backgroundColor: tokens.card.background,
    border: tokens.card.border,
    borderRadius: tokens.card.borderRadius,
    padding: tokens.card.padding,
    backdropFilter: tokens.card.backdropFilter,
    WebkitBackdropFilter: tokens.card.WebkitBackdropFilter,
    boxShadow: tokens.card.boxShadow,
    transition: 'box-shadow 0.22s ease, transform 0.22s ease',
    '&:hover': {
      boxShadow: tokens.card.boxShadowHover,
      transform: 'translateY(-1px)',
    },
  });

  const getHeaderSx = (): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 1.5,
    gap: 2,
  });

  const getTitleSx = (): SxProps<Theme> => ({
    fontSize: tokens.header.title.fontSize,
    fontWeight: tokens.header.title.fontWeight,
    color: tokens.header.title.color,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flex: 1,
    lineHeight: 1.4,
  });

  const getIndexBadgeSx = (): SxProps<Theme> => ({
    backgroundColor: tokens.header.indexBadge.background,
    color: tokens.header.indexBadge.color,
    fontSize: tokens.header.indexBadge.fontSize,
    padding: tokens.header.indexBadge.padding,
    borderRadius: tokens.header.indexBadge.borderRadius,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const getTimestampSx = (): SxProps<Theme> => ({
    fontSize: tokens.header.timestamp.fontSize,
    color: tokens.header.timestamp.color,
    marginTop: 0.75,
    marginLeft: 0.5,
  });

  const getExpandButtonSx = (): SxProps<Theme> => ({
    minWidth: 'auto',
    padding: '4px',
    color: tokens.header.expandButton.color,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  });

  const getFieldContainerSx = (): SxProps<Theme> => ({
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.field.spacing,
    marginTop: 2.5,
  });

  const getFieldRowSx = (): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
  });

  const getFieldLabelSx = (): SxProps<Theme> => ({
    fontSize: tokens.field.label.fontSize,
    color: tokens.field.label.color,
    fontWeight: tokens.field.label.fontWeight,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const getActionContainerSx = (): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
    marginTop: 2.5,
  });

  const getActionButtonSx = (color?: string): SxProps<Theme> => ({
    fontSize: tokens.action.button.fontSize,
    padding: tokens.action.button.padding,
    borderRadius: tokens.action.button.borderRadius,
    minWidth: tokens.action.button.minWidth,
    ...(color && {
      backgroundColor: color,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: color,
        opacity: 0.9,
      },
    }),
  });

  const getDividerSx = (): SxProps<Theme> => ({
    borderColor: tokens.action.divider.color,
    margin: tokens.action.divider.margin,
  });

  const getRelatedEventsButtonSx = (): SxProps<Theme> => ({
    fontSize: tokens.relatedEvents.button.fontSize,
    color: tokens.relatedEvents.button.color,
    padding: tokens.relatedEvents.button.padding,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  });

  const getExpandSectionButtonSx = (): SxProps<Theme> => ({
    fontSize: tokens.expandSection.button.fontSize,
    color: tokens.expandSection.button.color,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  });

  return {
    tokens,
    getCardSx,
    getHeaderSx,
    getTitleSx,
    getIndexBadgeSx,
    getTimestampSx,
    getExpandButtonSx,
    getFieldContainerSx,
    getFieldRowSx,
    getFieldLabelSx,
    getActionContainerSx,
    getActionButtonSx,
    getDividerSx,
    getRelatedEventsButtonSx,
    getExpandSectionButtonSx,
  };
}
